<template>
   <v-container>
      <v-snackbar v-model="snackbar" :color="snackColor" bottom right :timeout="4000">
         {{ snackText }}
         <template #action="{ attrs }">
            <v-btn color="white" text v-bind="attrs" @click="snackbar = false">Cerrar</v-btn>
         </template>
      </v-snackbar>

      <!-- Dialog anular -->
      <v-dialog v-model="dialogAnular" persistent max-width="500">
         <v-card>
            <v-card-title class="text-h6">Anular Pago</v-card-title>
            <v-card-text>
               <p class="mb-1">
                  <strong>Carnet:</strong> {{ pagoSeleccionado && pagoSeleccionado.alumnoCarnet }}<br />
                  <strong>Concepto:</strong> {{ pagoSeleccionado && pagoSeleccionado.concepto }}<br />
                  <strong>Monto:</strong> ${{ pagoSeleccionado && pagoSeleccionado.monto }}
               </p>
               <v-textarea
                  v-model="motivoAnulacion"
                  label="Motivo de anulación"
                  :rules="[v => !!v || 'Ingrese el motivo']"
                  rows="3"
                  outlined
                  class="mt-3"
               ></v-textarea>
            </v-card-text>
            <v-card-actions>
               <v-spacer></v-spacer>
               <v-btn text @click="dialogAnular = false">Cancelar</v-btn>
               <v-btn color="error" :disabled="!motivoAnulacion" :loading="loadingAnular" @click="confirmarAnular">
                  Anular
               </v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <!-- Animación envío de correo -->
      <v-dialog v-model="dialogCorreo" persistent max-width="360">
         <v-card class="text-center py-6 px-4">
            <div class="correo-wrapper mb-4">
               <v-icon class="correo-icono" size="64" color="primary">fas fa-envelope</v-icon>
               <v-icon class="check-icono" size="32" color="success">fas fa-check-circle</v-icon>
            </div>
            <div class="text-h6 font-weight-bold mb-1">Notificación enviada</div>
            <div class="body-2 grey--text">
               Se ha notificado al departamento de contabilidad sobre la anulación del pago.
            </div>
         </v-card>
      </v-dialog>

      <!-- Dialog reactivar -->
      <v-dialog v-model="dialogReactivar" persistent max-width="450">
         <v-card>
            <v-card-title class="text-h6">Reactivar Pago</v-card-title>
            <v-card-text>
               ¿Está seguro que desea reactivar este pago?
               <p class="mt-2">
                  <strong>Carnet:</strong> {{ pagoAnuladoSeleccionado && pagoAnuladoSeleccionado.alumnoCarnet }}<br />
                  <strong>Concepto original:</strong> {{ pagoAnuladoSeleccionado && pagoAnuladoSeleccionado.concepto }}
               </p>
            </v-card-text>
            <v-card-actions>
               <v-spacer></v-spacer>
               <v-btn text @click="dialogReactivar = false">Cancelar</v-btn>
               <v-btn color="primary" :loading="loadingReactivar" @click="confirmarReactivar">
                  Reactivar
               </v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <v-card elevation="0" outlined>
         <v-toolbar color="primary" dark>
            <v-toolbar-title>Historial de Pagos</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
               v-if="accessCode"
               dark
               text
               @click="irColecturiaDigital"
            >
               <v-icon left>fas fa-store</v-icon>
               Colecturía Digital
            </v-btn>
         </v-toolbar>

         <!-- Filtros -->
         <v-card-text class="pb-1">
            <v-row dense>
               <v-col cols="12" sm="6">
                  <v-text-field
                     v-model="filtroCarnet"
                     label="Carnet del alumno"
                     clearable
                     outlined
                     dense
                     @keyup.enter="buscar"
                  ></v-text-field>
               </v-col>
               <v-col cols="12" sm="3">
                  <v-autocomplete
                     v-model="filtroCiclo"
                     :items="ciclos"
                     item-text="label"
                     item-value="value"
                     label="Ciclo"
                     clearable
                     outlined
                     dense
                  ></v-autocomplete>
               </v-col>
               <v-col cols="12" sm="3" class="d-flex align-center">
                  <v-btn color="primary" block :loading="loadingBuscar" @click="buscar">
                     <v-icon left>fas fa-search</v-icon> Buscar
                  </v-btn>
               </v-col>
            </v-row>
            <v-row align="center" dense class="mt-0">
               <v-col cols="auto" class="py-0 pr-1">
                  <span class="caption grey--text text--darken-2 font-weight-medium">Año</span>
               </v-col>
               <v-col class="py-0">
                  <v-chip-group v-model="filtroAnnio" multiple>
                     <v-chip
                        v-for="a in annios"
                        :key="a"
                        :value="a"
                        small
                        filter
                        color="primary"
                        :outlined="!filtroAnnio.includes(a)"
                     >{{ a }}</v-chip>
                  </v-chip-group>
               </v-col>
            </v-row>
         </v-card-text>

         <v-tabs v-model="tab" class="tabs-outline px-4 pt-3 pb-1">
            <v-tab>Pagos</v-tab>
            <v-tab>Pagos Anulados</v-tab>
         </v-tabs>

         <v-tabs-items v-model="tab">
            <!-- TAB: PAGOS ACTIVOS -->
            <v-tab-item>
               <div class="tabla-scroll">
               <v-data-table
                  :headers="headersPagos"
                  :items="pagos"
                  :loading="loadingBuscar"
                  :items-per-page="15"
                  :footer-props="{ itemsPerPageText: 'Filas por página:' }"
                  class="elevation-0 tabla-compacta"
               >
                  <template #item.monto="{ item }">
                     ${{ item.monto }}
                  </template>
                  <template #item.opciones="{ item }">
                     <v-btn
                        small
                        color="error"
                        text
                        @click="abrirDialogAnular(item)"
                     >
                        <v-icon small>fas fa-ban</v-icon>
                        <span class="ml-1">Anular</span>
                     </v-btn>
                  </template>
               </v-data-table>
               </div>
            </v-tab-item>

            <!-- TAB: PAGOS ANULADOS -->
            <v-tab-item>
               <div class="tabla-scroll">
               <v-data-table
                  :headers="headersPagosAnulados"
                  :items="pagosAnulados"
                  :loading="loadingAnulados"
                  :items-per-page="15"
                  :footer-props="{ itemsPerPageText: 'Filas por página:' }"
                  class="elevation-0 tabla-compacta tabla-anulados-dense"
                  dense
               >
                  <template #item.monto="{ item }">
                     ${{ item.monto }}
                  </template>
                  <template #item.usuarioAnulacion="{ item }">
                     {{ item.usuarioAnulacion || '—' }}
                  </template>
                  <template #item.fechaReversion="{ item }">
                     {{ item.fechaReversion || '—' }}
                  </template>
                  <template #item.usuarioReactivacion="{ item }">
                     {{ item.usuarioReactivacion || '—' }}
                  </template>
                  <template #item.opciones="{ item }">
                     <v-btn
                        v-if="!item.fechaReversion"
                        small
                        color="success"
                        text
                        @click="abrirDialogReactivar(item)"
                     >
                        <v-icon small>fas fa-undo</v-icon>
                        <span class="ml-1">Reactivar</span>
                     </v-btn>
                  </template>
               </v-data-table>
               </div>
            </v-tab-item>
         </v-tabs-items>
      </v-card>
   </v-container>
</template>

<script>
import axios from 'axios'
import { authComputed } from '../helpers'

function serializeParams(params) {
   return Object.entries(params)
      .flatMap(([k, v]) => Array.isArray(v) ? v.map(i => `${k}=${encodeURIComponent(i)}`) : [`${k}=${encodeURIComponent(v)}`])
      .join('&')
}


export default {
   name: 'HistorialDePagos',
   computed: {
      ...authComputed
   },
   data: () => ({
      tab: 0,
      filtroCarnet: '',
      filtroAnnio: [],
      filtroCiclo: null,
      pagos: [],
      pagosAnulados: [],
      loadingBuscar: false,
      loadingAnulados: false,
      snackbar: false,
      snackText: '',
      snackColor: 'success',
      dialogAnular: false,
      motivoAnulacion: '',
      pagoSeleccionado: null,
      loadingAnular: false,
      dialogCorreo: false,
      dialogReactivar: false,
      pagoAnuladoSeleccionado: null,
      loadingReactivar: false,
      accessCode: '',
      annios: Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i),
      ciclos: [
         { label: 'Ciclo I', value: 1 },
         { label: 'Ciclo II', value: 2 }
      ],
      headersPagos: [
         { text: 'Carnet',       value: 'alumnoCarnet', width: '110px' },
         { text: 'Fecha',        value: 'fecha',        width: '140px' },
         { text: 'Concepto',     value: 'concepto',     width: '220px' },
         { text: 'Monto',        value: 'monto',        width: '90px' },
         { text: 'Autorización', value: 'autorizacion', width: '130px' },
         { text: 'Acciones',     value: 'opciones',     width: '100px', sortable: false }
      ],
      headersPagosAnulados: [
         { text: 'Carnet',          value: 'alumnoCarnet',        width: '90px' },
         { text: 'Fecha Pago',      value: 'fecha',               width: '110px' },
         { text: 'Concepto',        value: 'concepto',            width: '150px' },
         { text: 'Monto',           value: 'monto',               width: '70px' },
         { text: 'Fecha Anulación', value: 'fechaAnulacion',      width: '110px' },
         { text: 'Anulado por',     value: 'usuarioAnulacion',    width: '110px' },
         { text: 'Fecha Reversión', value: 'fechaReversion',      width: '110px' },
         { text: 'Reactivado por',  value: 'usuarioReactivacion', width: '110px' },
         { text: 'Acciones',        value: 'opciones',            width: '90px', sortable: false }
      ]
   }),
   created() {
      this.cargarAnulados()
   },
   methods: {
      buscar() {
         if (!this.filtroCarnet) {
            this.snackText = 'Ingrese un carnet para buscar.'
            this.snackColor = 'warning'
            this.snackbar = true
            return
         }
         this.accessCode = ''
         this.loadingBuscar = true
         const params = { carnet: this.filtroCarnet }
         if (this.filtroAnnio && this.filtroAnnio.length > 0) params.annio = this.filtroAnnio
         if (this.filtroCiclo) params.ciclo = this.filtroCiclo

         axios
            .get('api/PagoAnulado/Pagos', { params, paramsSerializer: p => serializeParams(p) })
            .then((r) => { this.pagos = r.data })
            .catch(() => {
               this.snackText = 'Error al obtener los pagos.'
               this.snackColor = 'error'
               this.snackbar = true
            })
            .finally(() => { this.loadingBuscar = false })

         this.cargarAnulados()


         // Obtener accessCode para el enlace de Colecturía Digital
         axios
            .get('api/Alumno/Credenciales?carnet=' + this.filtroCarnet)
            .then((r) => {
               if (r.data && r.data[0]) this.accessCode = r.data[0].accessCode
            })
            .catch(() => {})
      },
      cargarAnulados() {
         this.loadingAnulados = true
         const params = {}
         if (this.filtroAnnio && this.filtroAnnio.length > 0) params.annio = this.filtroAnnio
         if (this.filtroCiclo) params.ciclo = this.filtroCiclo
         if (this.filtroCarnet) params.carnet = this.filtroCarnet

         axios
            .get('api/PagoAnulado/Anulados', { params, paramsSerializer: p => serializeParams(p) })
            .then((r) => { this.pagosAnulados = r.data })
            .catch(() => {
               this.snackText = 'Error al obtener los pagos anulados.'
               this.snackColor = 'error'
               this.snackbar = true
            })
            .finally(() => { this.loadingAnulados = false })
      },
      abrirDialogAnular(pago) {
         this.pagoSeleccionado = pago
         this.motivoAnulacion = ''
         this.dialogAnular = true
      },
      confirmarAnular() {
         this.loadingAnular = true
         axios
            .post('api/PagoAnulado/Anular', {
               pagoId: this.pagoSeleccionado.pagoId,
               concepto: this.motivoAnulacion,
               usuario: this.oidcUser ? this.oidcUser.name : 'Desconocido'
            })
            .then(() => {
               this.dialogAnular = false
               this.buscar()
               this.cargarAnulados()
               // Mostrar animación de correo enviado y cerrarla sola a los 3s
               this.dialogCorreo = true
               setTimeout(() => { this.dialogCorreo = false }, 3000)
            })
            .catch(() => {
               this.snackText = 'Error al anular el pago.'
               this.snackColor = 'error'
               this.snackbar = true
            })
            .finally(() => { this.loadingAnular = false })
      },
      abrirDialogReactivar(pagoAnulado) {
         this.pagoAnuladoSeleccionado = pagoAnulado
         this.dialogReactivar = true
      },
      irColecturiaDigital() {
         window.open(
            process.env.VUE_APP_CLIENTE +
               'colecturiaDigital?carnet=' +
               this.filtroCarnet.trim().toUpperCase() +
               '&accesscode=' +
               this.accessCode,
            '_blank'
         )
      },
      confirmarReactivar() {
         this.loadingReactivar = true
         axios
            .post(`api/PagoAnulado/Reactivar/${this.pagoAnuladoSeleccionado.pagoAnuladoId}?usuario=${encodeURIComponent(this.oidcUser ? this.oidcUser.name : 'Desconocido')}`)
            .then(() => {
               this.snackText = 'Pago reactivado correctamente.'
               this.snackColor = 'success'
               this.snackbar = true
               this.dialogReactivar = false
               this.cargarAnulados()
               if (this.filtroCarnet) this.buscar()
            })
            .catch(() => {
               this.snackText = 'Error al reactivar el pago.'
               this.snackColor = 'error'
               this.snackbar = true
            })
            .finally(() => { this.loadingReactivar = false })
      }
   }
}
</script>

<style scoped>
/* ── Tabs como botones outline ── */
.tabs-outline ::v-deep .v-tabs-bar {
   background-color: transparent !important;
   height: auto !important;
}

.tabs-outline ::v-deep .v-tab {
   border: 2px solid #6C1C19 !important;
   border-radius: 6px !important;
   margin-right: 8px !important;
   color: #6C1C19 !important;
   background-color: #FFFFFF !important;
   font-weight: 600 !important;
   min-width: 130px !important;
   height: 36px !important;
   text-transform: none !important;
   letter-spacing: 0 !important;
}

.tabs-outline ::v-deep .v-tab--active {
   background-color: #6C1C19 !important;
   color: #FFFFFF !important;
}

.tabs-outline ::v-deep .v-tab:hover:not(.v-tab--active) {
   background-color: rgba(108, 28, 25, 0.08) !important;
}

.tabs-outline ::v-deep .v-tabs-slider-wrapper {
   display: none !important;
}


/* Scroll horizontal para tablas con muchas columnas */
.tabla-scroll {
   width: 100%;
   overflow-x: auto;
}

/* Evita que el texto desborde las celdas */
.tabla-compacta ::v-deep td,
.tabla-compacta ::v-deep th {
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
   max-width: 220px;
}

/* La columna concepto puede mostrar más texto */
.tabla-compacta ::v-deep td:nth-child(3) {
   white-space: normal;
   word-break: break-word;
}

/* Tabla de anulados: fuente y celdas más pequeñas para caber en pantalla */
.tabla-anulados-dense ::v-deep td,
.tabla-anulados-dense ::v-deep th {
   font-size: 11px !important;
   padding: 0 8px !important;
   max-width: 160px;
}

.tabla-anulados-dense ::v-deep .v-btn {
   font-size: 10px !important;
   padding: 0 4px !important;
   height: 24px !important;
   min-width: unset !important;
}

.correo-wrapper {
   position: relative;
   display: inline-block;
}

.correo-icono {
   animation: volar 0.7s ease-out forwards;
}

.check-icono {
   position: absolute;
   bottom: -6px;
   right: -10px;
   animation: aparecer 0.4s ease-out 0.6s both;
}

@keyframes volar {
   0%   { transform: translateY(20px) scale(0.7); opacity: 0; }
   60%  { transform: translateY(-8px) scale(1.1); opacity: 1; }
   100% { transform: translateY(0)    scale(1);   opacity: 1; }
}

@keyframes aparecer {
   0%   { transform: scale(0); opacity: 0; }
   100% { transform: scale(1); opacity: 1; }
}
</style>
